import { InProductTrialTypeEnum } from '@nirvana/shared/dtos/types/in-product-trials';
import { BaseWebComponentTranslator } from '../../../../../framework/BaseWebComponentTranslator';
import { AccordionItems, CardItem, GzLandingPageConfig } from '../../types';
import { AvailableProductTrialType } from '../../../../b2b/types/AvailableProductTrialType';
import { getButtonSettingsForMsp, getButtonsSettingsForProductItem } from './gz-landing-page-button-id';
import { AccordionItemLinks, MspAccordionItemLinks } from './gz-landing-page-links';
import { ImgPosition } from '../../../gzTrialProductItem/type';
import { InProductTrialCannotBeStartedReason } from '../../../../b2b/types/InProductTrialCannotBeStartedReason';
import { CountdownTranslations } from '../../../../../ui/gzCountDown/types';
import { getTranslatedInProductTrialType } from '../../../../b2b/utils/InProductTrialType';
import { getCommonDescriptionForInProductTrialItem } from '../../../../b2b/inProductTrialsApp/view/gzPageConfigs/gzCommonConfig/gz-common-page-config';
import {
  MspInProductTrialCannotBeStartedReason,
  MspInProductTrialId,
} from '../../../../../company/common/mspInProductTrialsTab/types/msp-in-product-trials-types';

// get paragraph
function getParagraph(text: string): string {
  return `<p fontSize="12" class="pb-2">${text}</p>`;
}

// get list of text items
function getListOfTextItems(textItems: Array<string>): string {
  let listOfItems = `<ul>`;

  for (const textItem of textItems) {
    listOfItems += `<li fontSize="12" class="pb-2">${textItem}</li>`;
  }

  return listOfItems + `</ul>`;
}

// get variable for gz link
function getGzLinkVar(href: string): string {
  return `<gz-link type="underline" color="primary" href="${href}"><span>`;
}

// get compliance manager faq accordion items
function getComplianceFaqAccordionItemsForProductItem(translator: BaseWebComponentTranslator) {
  return {
    requirements: {
      title: translator.translateFn('What requirements are needed to use Compliance Manager?'),
      description: getParagraph(
        translator.translateFn(
          'The solution, including a basic standard is available to all customers who have access to the Compliance Manager page and have Risk Management enabled. As part of monthly subscription licensing, the Compliance Manager is available as an add-on to any protection model-whether purchased a la carte or as one of the Secure, Secure Extra, or Secure Plus bundles.'
        )
      ),
    },
    licensingOptions: {
      title: translator.translateFn('What licensing options are available?'),
      description: getParagraph(
        translator.translateFn(
          'All customers with Compliance Manager access will automatically receive a basic standard, which includes best-practice guidelines, real-time insights, an exportable report, and preview access to advanced standards. Upgrading to a Compliance Manager add-on license unlocks advanced standards such as GDPR and NIS2, providing complete visibility into your compliance posture, detailed scoring, and comprehensive, exportable reports.'
        )
      ),
    },
    attackSurface: {
      title: translator.translateFn('Which attack surfaces does Compliance Manager monitor? '),
      description: getParagraph(
        translator.translateFn(
          'Currently, Compliance Manager monitors endpoints (workstations and servers). Support for additional assets and environments are coming in the near future.'
        )
      ),
    },
  };
}

// get common faq accordion items
function getCommonFaqAccordionItemsForProductItem(translator: BaseWebComponentTranslator): {
  subscriptionEndDate: AccordionItems;
  licensingStatus: AccordionItems;
  termsOfUse: AccordionItems;
  reactivation: AccordionItems;
  extraInformation: AccordionItems;
} {
  return {
    subscriptionEndDate: {
      title: translator.translateFn('Will the trial affect my subscription end date?'),
      description: getParagraph(translator.translateFn('No. The end date of your subscription will remain the same.')),
    },
    licensingStatus: {
      title: translator.translateFn("How will this impact my company's licensing status?"),
      description: getParagraph(
        translator.translateFn(
          "Your existing license keys will not be affected. We will add a new license key for you during the trial period and remove it when it's over."
        )
      ),
    },
    termsOfUse: {
      title: translator.translateFn(
        'Are there any changes in the terms of use and privacy policy that apply during the trial?'
      ),
      description: getParagraph(
        translator.translateFn(
          'All relevant information can be found under our {eulaBusinessSolutionLink}terms of use (EULA){/link} and {privacyPolicyLink}Privacy Policy{/link} help pages.',
          {
            eulaBusinessSolutionLink: getGzLinkVar(AccordionItemLinks.EulaBusinessSolution),
            privacyPolicyLink: getGzLinkVar(AccordionItemLinks.PrivacyPolicy),
            '/link': '</span></gz-link>',
          }
        )
      ),
    },
    reactivation: {
      title: translator.translateFn(
        'I have less than 35 days left on my subscription. Can I still activate the in-product trial?'
      ),
      description: getParagraph(
        translator.translateFn(
          'Normally, the feature is not available in this situation. However, if you would still like to enroll, contact Bitdefender Enterprise Support and request them to activate the trial for your company.'
        )
      ),
    },
    extraInformation: {
      title: translator.translateFn('Where can I find more information regarding Product Trials?'),
      description: getParagraph(
        translator.translateFn('You can find a more comprehensive FAQ article {productTrialsFaqLink}here{/link}.', {
          productTrialsFaqLink: getGzLinkVar(AccordionItemLinks.ProductTrialsFaq),
          '/link': '</span></gz-link>',
        })
      ),
    },
  };
}

// get common faq accordion items for msp
function getCommonFaqAccordionItemsForProductItemForMsp(translator: BaseWebComponentTranslator): {
  existingMonthlySubscription: AccordionItems;
  eligibleCompanies: AccordionItems;
  policyChanges: AccordionItems;
  mspIptInfo: AccordionItems;
} {
  return {
    existingMonthlySubscription: {
      title: translator.translateFn(
        "What will happen to my customer's monthly subscription during and after the trial?"
      ),
      description: getParagraph(
        translator.translateFn(
          "Once enrolled, the add-on is enabled for the selected client company. At the end of the trial, all changes done to the company's subscription will be reverted."
        )
      ),
    },
    eligibleCompanies: {
      title: translator.translateFn('What companies are eligible for the trial?'),
      description: getParagraph(
        translator.translateFn(
          "You can enroll companies meeting these requirements: the company type must be Customer; the license must be Monthly Subscription; the product type must be Endpoint Security; the protection model must be A la carte; the company's direct partner must have the feature available for resell."
        )
      ),
    },
    policyChanges: {
      title: translator.translateFn(
        'Are there any changes in the terms of use and privacy policy that apply during the trial?'
      ),
      description: getParagraph(
        translator.translateFn(
          'All relevant information is under our {eulaLink}terms of use (EULA){/link} and {privacyPolicyLink}Privacy Policy{/link}.',
          {
            eulaLink: getGzLinkVar(MspAccordionItemLinks.Eula),
            privacyPolicyLink: getGzLinkVar(MspAccordionItemLinks.PrivacyPolicy),
            '/link': '</span></gz-link>',
          }
        )
      ),
    },
    mspIptInfo: {
      title: translator.translateFn('Where can I find more information regarding MSP Product Trials?'),
      description: getParagraph(
        translator.translateFn('You can find a more comprehensive FAQ article {faqLink}here{/link}.', {
          faqLink: getGzLinkVar(MspAccordionItemLinks.FAQ),
          '/link': '</span></gz-link>',
        })
      ),
    },
  };
}

// get common card items
function getCommonCardItemsForProductItem(translator: BaseWebComponentTranslator): {
  instantRegistrationAddon: CardItem;
  easyToStart: CardItem;
  instantRegistrationBaseProduct: CardItem;
  forHelp: CardItem;
} {
  return {
    instantRegistrationAddon: {
      cardName: translator.translateFn('Instant registration'),
      text: translator.translateFn(
        "Start a trial and access new features instantly. The add-on will be added to your company's list of licenses as a separate product."
      ),
      icon: 'icon-trial_card_1',
    },
    instantRegistrationBaseProduct: {
      cardName: translator.translateFn('Instant registration'),
      text: translator.translateFn(
        'Start a trial and access new features instantly. Your license will update automatically for the duration of the trial and revert to its original state when the trial ends.'
      ),
      icon: 'icon-trial_card_1',
    },
    easyToStart: {
      cardName: translator.translateFn('Easy to start'),
      text: translator.translateFn(
        'Once you start the trial, we will handle the licensing changes needed for the selected company. At the end of the trial period, licensing will return to its previous state.'
      ),
      icon: 'icon-trial_card_1',
    },
    forHelp: {
      cardName: translator.translateFn('We are here to help'),
      text: translator.translateFn(
        'If you have any questions or feedback regarding the new capabilities, contact our Enterprise Support team, and we will help.'
      ),
      icon: 'icon-trial_card_3',
    },
  };
}

// configurations for available product trials items in landing page
function getProductTrialTypeToLandingPageConfigMapping(translator: BaseWebComponentTranslator): {
  [type in AvailableProductTrialType]: GzLandingPageConfig;
} {
  const buttonsSettings = getButtonsSettingsForProductItem(translator);

  // common items
  const commonAccordionItemsFaq = getCommonFaqAccordionItemsForProductItem(translator);
  const complianceAccordionItemsFaq = getComplianceFaqAccordionItemsForProductItem(translator);
  const commonCardItems = getCommonCardItemsForProductItem(translator);
  const descriptions = getCommonDescriptionForInProductTrialItem(translator);

  // common items xdr
  const commonEasySetupCardItemXdr = {
    cardName: translator.translateFn('Easy setup'),
    text: translator.translateFn(
      'Select a few endpoints where you want to test the new feature, and make sure they all have the BEST agent installed with the {bold}EDR sensor{/bold} module included. Create a new policy with the feature enabled under the {bold}Incident Sensor{/bold} page and apply it to the same endpoints. To finish, integrate your sensors from the {bold}Configuration{/bold} page, in the {bold}Sensor Management tab{/bold}.',
      {
        bold: '<strong>',
        '/bold': '</strong>',
      }
    ),
    icon: 'icon-trial_card_2',
  };
  const commonAccordionItemsXdr = [
    {
      title: translator.translateFn(
        "Improve incident investigations with context delivered to your analyst's fingertips"
      ),
      description: getParagraph(
        translator.translateFn(
          'Take advantage of automated and guided investigations of incidents. Enjoy automated root cause analysis enhanced with correlated event data from across your environment.'
        )
      ),
      isOpen: true,
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/xdr/incidentInvestigation.jpg',
      },
    },
    {
      title: translator.translateFn('Single-click Response executed directly from within the XDR platform'),
      description: getParagraph(
        translator.translateFn(
          'You get out-of-the-box responses across endpoints, identities, email, cloud applications, and networks to rapidly respond to threats from one integrated console.'
        )
      ),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/xdr/singleClickResponse.jpg',
      },
    },
    {
      title: translator.translateFn(
        'Automated threat identification, triage, prioritization, and response designed for teams with varying skill sets'
      ),
      description: getParagraph(
        translator.translateFn(
          'Employ industry-leading prevention and detection methods to a broad set of data sources. We provide multi-tier correlation and detection algorithms delivered locally to the sensor and cloud platform level. Additionally, we enable security teams to create their own detection rules easily.'
        )
      ),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/xdr/automatedThreatIdentification.jpg',
      },
    },
    {
      title: translator.translateFn('All the data you need to confidently take action from one screen'),
      description: getParagraph(
        translator.translateFn(
          'GravityZone Incident Advisor provides an overview of all key information to minimize the time required to investigate and respond. This summary gives you an actionable view of each step in an attack, the organizational impact, and any correlated incidents.'
        )
      ),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/xdr/allData.jpg',
      },
    },
  ];
  const commonAccordionItemFaqReactivationXdr = {
    title: translator.translateFn(
      'I have less than 65 days left on my subscription. Can I still activate the in-product trial?'
    ),
    description: commonAccordionItemsFaq.reactivation.description,
  };

  // common items next gen
  const commonCardItemsNextGen = [
    commonCardItems.instantRegistrationBaseProduct,
    {
      cardName: translator.translateFn('Easy setup'),
      text: translator.translateFn(
        'Create a new policy that applies the newly available capabilities. Select the target endpoints for testing the new protection features, and then apply the policy.'
      ),
      icon: 'icon-trial_card_2',
    },
    commonCardItems.forHelp,
  ];
  const commonAccordionItemsNextGen = [
    {
      title: translator.translateFn('Integrated risk analytics'),
      description: getParagraph(
        translator.translateFn(
          'Continuously monitor resources for risks to uncover and prioritize findings and enable automatic hardening actions to {bold}remedy vulnerabilities{/bold}. Identify user actions and behavior that pose a {bold}security risk{/bold} to your organization, including {bold}logging into insecure websites, poor password management, and compromised USB usage{/bold}.',
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        )
      ),
      isOpen: true,
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/bsPremium/IntegratedRiskAnalytics.png',
      },
    },
    {
      title: translator.translateFn('Ransomware prevention and mitigation'),
      description:
        getParagraph(
          translator.translateFn(
            'Fighting ransomware requires understanding the full cyber kill chain and mapping defenses for each attack stage.'
          )
        ) +
        getParagraph(
          translator.translateFn(
            '{bold}Ransomware prevention and mitigation{/bold} is built into the GravityZone management console and consists of:',
            {
              bold: '<strong>',
              '/bold': '</strong>',
            }
          )
        ) +
        getListOfTextItems([
          translator.translateFn(
            'Up-to-date tamperproof backup copies of user files created automatically without using shadow copies.'
          ),
          translator.translateFn(
            'Blocking and prevention capabilities ({filelessAttackDefenseLink}Fileless Attack Defense{/link}; {networkAttackDefenseLink}Network Attack Defense{/link}; Advanced Anti-Exploit; Machine Learning Anti-Malware).',
            {
              filelessAttackDefenseLink: getGzLinkVar(AccordionItemLinks.FilelessAttackDefense),
              networkAttackDefenseLink: getGzLinkVar(AccordionItemLinks.NetworkAttackDefense),
              '/link': '</span></gz-link>',
            }
          ),
          translator.translateFn(
            'Multiple detection layers ({processInspectionLink}Process inspection{/link}, registry monitoring, code inspection, {hyperDetectLink}Hyper Detect{/link}).',
            {
              processInspectionLink: getGzLinkVar(AccordionItemLinks.ProcessInspection),
              hyperDetectLink: getGzLinkVar(AccordionItemLinks.HyperDetect),
              '/link': '</span></gz-link>',
            }
          ),
          translator.translateFn('User and system risk mitigation technologies.'),
        ]),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/bsPremium/RansomwarePreventionMitigation.png',
      },
    },
    {
      title: translator.translateFn('Human risk analytics'),
      description:
        getParagraph(
          translator.translateFn(
            "Helps identify user actions and behavior that pose a security risk to the organization, such as using unencrypted web pages for logging into websites, poor password management, usage of compromised USBs in the organization's network, recurrent infections, to name a few."
          )
        ) +
        getParagraph(
          translator.translateFn(
            'By placing the human element in the middle of the risk analytics and management strategy, your organization becomes even harder to breach.'
          )
        ),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/bsPremium/HumanRiskAnalytics.png',
      },
    },
    {
      title: translator.translateFn('End to end attack forensics'),
      description: getParagraph(
        translator.translateFn(
          "{attackForensicsAndVisualizationLink}Attack forensics and visualization{/link} enhance the level of visibility into your organization's threat landscape and reveals the broader context of attacks on endpoints. It lets you zero in on specific threats and take corrective action.",
          {
            attackForensicsAndVisualizationLink: getGzLinkVar(AccordionItemLinks.AttackForensicsAndVisualization),
            '/link': '</span></gz-link>',
          }
        )
      ),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/bsPremium/endToEndAttackForensics.png',
      },
    },
    {
      title: translator.translateFn('Network Attack Defense'),
      description: getParagraph(
        translator.translateFn(
          'Detect and prevent network vulnerability attacks. Network Attack Defense blocks network attacks including brute force, password stealers, and lateral movement before they can execute. Network Attack Defense is an important source of information for EDR incident correlations.'
        )
      ),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/bsPremium/NetworkAttackDefense.jpeg',
      },
    },
    {
      title: translator.translateFn('Layered defense'),
      description: getParagraph(
        translator.translateFn(
          'Signature-less technologies, including advanced local and cloud machine learning, behavior analysis, integrated sandbox, and device hardening work as highly effective, layered protection against sophisticated threats.'
        )
      ),
      image: {
        alt: 'Placeholder',
        src: 'app/webcomponents/assets/images/trial/bsEnterprise/LayeredDefense.jpeg',
      },
    },
  ];
  const commonAccordionItemsFaqBaseProduct = [
    commonAccordionItemsFaq.subscriptionEndDate,
    commonAccordionItemsFaq.termsOfUse,
    commonAccordionItemsFaq.reactivation,
    commonAccordionItemsFaq.extraInformation,
  ];

  return {
    [InProductTrialTypeEnum.OfficeSecurityPack]: {
      productItem: {
        type: InProductTrialTypeEnum.OfficeSecurityPack,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.OfficeSecurityPack),
        description: descriptions[InProductTrialTypeEnum.OfficeSecurityPack],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/bs/bsHeader.jpg',
      },
      cardItems: commonCardItemsNextGen,
      accordionItems: [
        {
          title: translator.translateFn('Layered Protection for Endpoints'),
          description:
            getParagraph(
              translator.translateFn(
                'Secure desktops, laptops, and servers using multi-tiered security to achieve precise malware detection with minimal false alarms.'
              )
            ) +
            getParagraph(
              translator.translateFn(
                '{productName} utilizes an advanced, multi-layered endpoint protection system that boasts top-tier prevention, detection, and blocking features. This is achieved through proven machine learning techniques, behavioral analysis and continuous monitoring of running processes.',
                {
                  productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.OfficeSecurityPack),
                }
              )
            ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bs/layeredProtection.jpg',
          },
        },
        {
          title: translator.translateFn('Ransomware Prevention and Mitigation'),
          description:
            getParagraph(
              translator.translateFn(
                'Defeating ransomware means identifying every step of the attack process and aligning defenses accordingly.'
              )
            ) +
            getParagraph(
              translator.translateFn(
                '{productName} offers users and security admins tools at multiple levels to keep ransomware at bay:',
                {
                  productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.OfficeSecurityPack),
                }
              )
            ) +
            getListOfTextItems([
              translator.translateFn(
                'Pre-emptive protection by creating automatic, up-to-date tamperproof backup copies of user files.'
              ),
              translator.translateFn(
                'Blocking and prevention at endpoint and network level, on pre-execution and at runtime.'
              ),
              translator.translateFn(
                'Real-time monitoring of running processes to identify suspicious or malicious processes for automatic or manual termination.'
              ),
              translator.translateFn(
                "Risk mitigation to ensure organizations aren't left susceptible to ransomware attacks by system and application vulnerabilities and findings or risky user behavior."
              ),
            ]),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bs/ransomwarePrevention.jpg',
          },
        },
        {
          title: translator.translateFn('Network Attack Defense'),
          description:
            getParagraph(
              translator.translateFn(
                'Bitdefender Network Attack Defense is a powerful technology focused on preventing an array of attacks leveraging the host network to target sensitive assets and information.'
              )
            ) +
            getParagraph(
              translator.translateFn(
                'This approach broadens security coverage through network-centric defenses, effectively neutralizing both familiar and unfamiliar threats, such as brute force attacks, password theft tools, network vulnerabilities, and unauthorized lateral movements, before they pose a risk.'
              )
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bs/network.jpg',
          },
        },
        {
          title: translator.translateFn('Endpoint Risk Analytics'),
          description:
            getParagraph(
              translator.translateFn(
                'Effectively identifying, assessing, and remediating endpoint weaknesses is crucial for a strong cybersecurity plan and lowering potential risks.'
              )
            ) +
            getParagraph(
              translator.translateFn(
                "Bitdefender's Endpoint Risk Analytics consistently watches for resource vulnerabilities, highlighting and addressing findings with automatic fixes. It identifies user actions and behavior that pose a security risk to your organization, including logging into insecure websites, poor password management, and compromised USB usage."
              )
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bs/endpointRisk.jpg',
          },
        },
        {
          title: translator.translateFn('Device Control'),
          description: getParagraph(
            translator.translateFn(
              "Bitdefender's Device Control provides a robust safeguard against both data leaks and malware threats originating from external devices connected to endpoints. By implementing specific blocking rules and exceptions through policies, it effectively manages a wide array of device types, including USB drives, Bluetooth gadgets, CD/DVD drives, and various storage peripherals."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bs/deviceControl.jpg',
          },
        },
        {
          title: translator.translateFn('Web Access Control'),
          description: getParagraph(
            translator.translateFn(
              "Bitdefender's Web Access Control empowers you to regulate and manage internet access for both users and applications based on designated time frames. When Web Access Control restricts access to certain web pages, those pages won't appear in the browser as usual. Instead, users will see a standard notification page, indicating that their attempt to access the specific website has been restricted by Web Access Control."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bs/webAccessControl.jpg',
          },
        },
      ],
      accordionItemsFaq: commonAccordionItemsFaqBaseProduct,
    },
    [InProductTrialTypeEnum.NextGen]: {
      productItem: {
        type: InProductTrialTypeEnum.NextGen,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.NextGen),
        description: descriptions[InProductTrialTypeEnum.NextGen],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/bsPremium/bsPremiumHeader.png',
      },
      cardItems: commonCardItemsNextGen,
      accordionItems: commonAccordionItemsNextGen,
      accordionItemsFaq: commonAccordionItemsFaqBaseProduct,
    },
    [InProductTrialTypeEnum.NextGenWithReconfigurePopup]: {
      productItem: {
        type: InProductTrialTypeEnum.NextGenWithReconfigurePopup,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.NextGenWithReconfigurePopup),
        description: descriptions[InProductTrialTypeEnum.NextGenWithReconfigurePopup],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/bsPremium/bsPremiumHeader.png',
      },
      cardItems: commonCardItemsNextGen,
      accordionItems: commonAccordionItemsNextGen,
      accordionItemsFaq: commonAccordionItemsFaqBaseProduct,
    },
    [InProductTrialTypeEnum.Atd]: {
      productItem: {
        type: InProductTrialTypeEnum.Atd,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.Atd),
        description: descriptions[InProductTrialTypeEnum.Atd],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/bsEnterprise/bsEnterprise_header.jpeg',
      },
      cardItems: [
        commonCardItems.instantRegistrationBaseProduct,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            'Create a new policy that applies the newly available capabilities. Select the target endpoints for testing the new protection features, and then apply the policy. If you want to test Endpoint Detection and Response (EDR), make sure to add it using a Reconfigure agent task.'
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Cross-endpoint correlation'),
          description: getParagraph(
            translator.translateFn(
              'Take threat detection and visibility to a new level by applying XDR capabilities for detecting advanced attacks across multiple endpoints in hybrid infrastructures (workstations, servers, and containers, running on various OS).'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bsEnterprise/CrossEndpointCorrelation.jpeg',
          },
        },
        {
          title: translator.translateFn('Low overhead incident detection and response'),
          description: getParagraph(
            translator.translateFn(
              "Quickly triage alerts and investigate incidents using GravityZone Business Security Enterprise's attack timeline and sandbox output. Enable incident response teams to react swiftly and stop ongoing attacks with a single click."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bsEnterprise/LowOverheadIncident.jpeg',
          },
        },
        {
          title: translator.translateFn('Integrated human and riskpoint analytics'),
          description: getParagraph(
            translator.translateFn(
              'Continuously analyze risk using hundreds of factors to uncover, prioritize and automatically enable hardening actions to remedy configuration risks to all your endpoints. Identify user behavior that pose a security risk to your organization, including logging into insecure websites, poor password management, and compromised USB usage.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bsEnterprise/IntegratedHuman.jpeg',
          },
        },
        {
          title: translator.translateFn('Modern next-gen prevention and detection with automatic remediation'),
          description: getParagraph(
            translator.translateFn(
              'Industry leading prevention meets on-execution behavior-based detection capabilities to prevent and stop advanced threats from being executed on enterprise infrastructure. Once an active threat is detected, an automatic response kicks in for blocking further damage or lateral movements.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bsEnterprise/NextGenPrevention.jpeg',
          },
        },
        {
          title: translator.translateFn('Cross platform coverage and 3rd party integration APIs'),
          description: getParagraph(
            translator.translateFn(
              'Deliver consistent security across all enterprise endpoints running Windows, Linux, or macOS in physical, virtualized, or cloud infrastructures. Supports integration with pre-existing security operations tools, including Splunk, and is optimized for datacenter technologies, including all major hypervisors.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bsEnterprise/CrossPlatformCoverage.jpeg',
          },
        },
        {
          title: translator.translateFn('Layered defense'),
          description: getParagraph(
            translator.translateFn(
              'Signature-less technologies, including advanced local and cloud machine learning, behavior analysis, integrated sandbox, and device hardening work as highly effective, layered protection against sophisticated threats.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/bsEnterprise/LayeredDefense.jpeg',
          },
        },
      ],
      accordionItemsFaq: commonAccordionItemsFaqBaseProduct,
    },
    [InProductTrialTypeEnum.XdrFull]: {
      productItem: {
        type: InProductTrialTypeEnum.XdrFull,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.XdrFull),
        description: descriptions[InProductTrialTypeEnum.XdrFull],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/xdr/xdrHeader.jpg',
      },
      cardItems: [
        {
          cardName: translator.translateFn('Instant registration'),
          text: translator.translateFn(
            'Start a trial and access new sensors instantly. You will get new license keys for all unlicensed sensor types during the trial period and they will be removed when the trial ends.'
          ),
          icon: 'icon-trial_card_1',
        },
        commonEasySetupCardItemXdr,
        commonCardItems.forHelp,
      ],
      accordionItems: commonAccordionItemsXdr,
      accordionItemsFaq: [
        {
          title: translator.translateFn("How will this impact my company's licensing status?"),
          description: getParagraph(
            translator.translateFn(
              "Your existing license keys will not be affected. We will add a new license key for {productName} and one more for each sensor type. The keys will remain for the duration of the trial and will be removed when it's over.",
              { productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.Atd) }
            )
          ),
        },
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemFaqReactivationXdr,
        commonAccordionItemsFaq.extraInformation,
        {
          title: translator.translateFn('What happens if I add a sensor license key during the trial?'),
          description: getParagraph(
            translator.translateFn('You cannot add a license key for a sensor until the trial ends.')
          ),
        },
      ],
    },
    [InProductTrialTypeEnum.XdrSensors]: {
      productItem: {
        type: InProductTrialTypeEnum.XdrSensors,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.XdrSensors),
        description: descriptions[InProductTrialTypeEnum.XdrSensors],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/xdr/xdrHeader.jpg',
      },
      cardItems: [
        {
          cardName: translator.translateFn('Instant registration'),
          text: translator.translateFn(
            'Start a trial and access new features instantly. You will get new license keys during the trial period and they will be removed when the trial ends.'
          ),
          icon: 'icon-trial_card_1',
        },
        commonEasySetupCardItemXdr,
        commonCardItems.forHelp,
      ],
      accordionItems: commonAccordionItemsXdr,
      accordionItemsFaq: [
        {
          title: translator.translateFn("How will this impact my company's licensing status?"),
          description: getParagraph(
            translator.translateFn(
              'Your existing license keys will not be affected. License keys are added for every sensor type currently not licensed. At the end of the trial, all existing trial license keys will be removed.'
            )
          ),
        },
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemFaqReactivationXdr,
        commonAccordionItemsFaq.extraInformation,
        {
          title: translator.translateFn('What happens if I add a sensor license key during the trial?'),
          description: getParagraph(
            translator.translateFn(
              'The corresponding key provided by the trial will be replaced with the new key and will remain in place until the expiry date. The rest of the keys will remain in place until the end of the trial. If you replace all the sensor trial keys, the trial will end.'
            )
          ),
        },
      ],
    },
    [InProductTrialTypeEnum.PatchManagement]: {
      productItem: {
        type: InProductTrialTypeEnum.PatchManagement,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.PatchManagement),
        description: descriptions[InProductTrialTypeEnum.PatchManagement],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/patchManagement/patchManagementHeader.png',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            "Create a maintenance window to configure automatic patch deployment for your company and add a new policy that applies the newly available capabilities. Choose the endpoints you'll be testing the new protection features on, deploy the Patch Management module using a Reconfigure agent task and apply the policy."
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Improved compliance'),
          description: getParagraph(
            translator.translateFn(
              'The patch statistics provide audit trails confirming security updates are applied within regulatory timelines. By using Maintenance Windows, you can schedule the installation of security and priority patches to ensure updates are completed.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/patchManagement/improveCompliance.jpg',
          },
        },
        {
          title: translator.translateFn('Flexible patch management options'),
          description: getParagraph(
            translator.translateFn(
              'Each patch scan automatically verifies that your software is running the latest patch with real-time patch statistics revealing missing or failed updates. Choose between automatic or one-click on-demand installation to patch multiple endpoints in minutes.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/patchManagement/flexiblePatchOptions.jpg',
          },
        },
        {
          title: translator.translateFn('Comprehensive knowledge about your patch status'),
          description: getParagraph(
            translator.translateFn(
              'Your patch inventory links to vendor and CVE bulletins and helps identify critical patches and vulnerability information. Configure a customized automated patching system for preferred applications, allowing for the scheduling of both security and non-security patches. Additionally, you gain the ability to postpone reboots to maintain complete control over your environment.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/patchManagement/comprehensiveKnowledge.png',
          },
        },
        {
          title: translator.translateFn('Increased confidence in your security posture'),
          description: getParagraph(
            translator.translateFn(
              'Patch statistics provide up-to-the-minute reports on patching including endpoint scan times and patch status. For each patch, an easy-to-read pie chart shows the installed, missed, failed, ignored, and pending patches.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/patchManagement/increaseConfidence.jpg',
          },
        },
        {
          title: translator.translateFn('Improved risk management'),
          description: getParagraph(
            translator.translateFn(
              'Using Patch Management you can quickly address the vulnerabilities highlighted by GravityZone Risk Analytics.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/patchManagement/improvedRiskManagement.png',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.VolumeEncryption]: {
      productItem: {
        type: InProductTrialTypeEnum.VolumeEncryption,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.VolumeEncryption),
        description: descriptions[InProductTrialTypeEnum.VolumeEncryption],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        isDemoAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/volumeEncryption/volumeEncryptionHeader.png',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            "Create a new policy that applies the newly available capabilities. Choose the endpoints you'll be testing the new protection features on, deploy the Full Disk Encryption module using a Reconfigure agent task and apply the policy."
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Protection against device loss or theft for remote devices'),
          description: getParagraph(
            translator.translateFn(
              'All drives are encrypted at rest with encryption management only available to security administrators through the GravityZone console.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/volumeEncryption/protectionAgainstDeviceLoss.jpg',
          },
        },
        {
          title: translator.translateFn('Native-proven encryption and ease of use'),
          description: getParagraph(
            translator.translateFn(
              'Ensure compatibility and performance without increasing workloads by employing the native encryption mechanisms provided by Windows ({bold}BitLocker{/bold}), Mac ({bold}FileVault{/bold}, and {bold}diskutil{/bold} command-line utility). Provide your security administrators with one-click encryption and decryption keys from the GravityZone console.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/volumeEncryption/nativeProvenEncryption.jpg',
          },
        },
        {
          title: translator.translateFn('Pre-boot authentication enforcement'),
          description: getParagraph(
            translator.translateFn(
              'Create a secure, tamper-proof environment external to the operating system as a trusted authentication layer. This prevents anything from being read from the hard disk until the user has confirmed they have the correct credentials.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/volumeEncryption/preBootAuthentication.jpg',
          },
        },
        {
          title: translator.translateFn('Prove continuous regulatory compliance and pass audits'),
          description: getParagraph(
            translator.translateFn(
              'Using native tools ensures data is encrypted according to Advanced Encryption Standards (AES) 128-bit and 256-bit encryption. Encryption-specific reporting provides an up-to-date inventory of the encrypted Windows and Mac endpoints in your network.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/volumeEncryption/proveContinuousCompliance.png',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.MobileSecurity]: {
      productItem: {
        type: InProductTrialTypeEnum.MobileSecurity,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.MobileSecurity),
        description: descriptions[InProductTrialTypeEnum.MobileSecurity],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/mobileSecurity/mobileSecurityHeader.png',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            'Activate the Mobile Security console, deploy the security app along on a few test devices, and configure the policies you want to apply to them.'
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      hasOutlineAroundAccordionItems: false,
      accordionItems: [
        {
          title: translator.translateFn('App-Web-Network-Device Protection'),
          description: getParagraph(
            translator.translateFn(
              'GravityZone Security for Mobile utilizes on-device machine learning technologies to detect both known and unknown threats by monitoring mobile device behavior, accurately identifying malware-like apps, anomalous network traffic, and phishing attacks, and providing protection even when the endpoint is offline.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mobileSecurity/appWebNetworkDevice.png',
          },
        },
        {
          title: translator.translateFn('Mobile Application Vetting'),
          description: getParagraph(
            translator.translateFn(
              'Helps assess the security and privacy posture of applications. Allows detection of non-compliant applications and maintaining a list of allowed applications and versions. It allows each business to design their own "application compliance" policy, build a catalogue of accepted apps and versions, and monitor endpoints through it. With Application Vetting, organizations can trigger actions (locally on MTD or via MDM) on out-of-compliance application detections.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mobileSecurity/mobileApplicationVetting.png',
          },
        },
        {
          title: translator.translateFn('Threat Hunting on Android, iOS and Chromebook Applications'),
          description: getParagraph(
            translator.translateFn(
              'Similar to traditional endpoints, not all tasks can be performed locally on the mobile device. To provide comprehensive security, GravityZone Security for Mobile conducts {bold}cloud-based deep analysis{/bold}, which delivers detailed technical reports on the analyzed applications and provides security teams with visibility into other versions of the application analyzed by the system, regardless of whether they were detected in their environment or not.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mobileSecurity/threatHunting.png',
          },
        },
        {
          title: translator.translateFn('Anti-phishing protection on all supported platforms'),
          description:
            getParagraph(
              translator.translateFn(
                'With email as the number one attack medium for phishing attacks (90%), and 60% of emails being opened on mobile devices, it is obvious that a phishing solution on the mobile device itself is mandatory.'
              )
            ) +
            getParagraph(
              translator.translateFn(
                'GravityZone Security for Mobile offers {bold}on-device anti-phishing protection{/bold} on all supported platforms (iOS, Android) and {bold}on-device, local VPN{/bold} for real-time processing of traffic without relying on an external service, thereby effectively preventing phishing attacks.',
                {
                  bold: '<strong>',
                  '/bold': '</strong>',
                }
              )
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mobileSecurity/antiPhishing.png',
          },
        },
        {
          title: translator.translateFn('End-to-End Visibility'),
          description: getParagraph(
            translator.translateFn(
              'On top of monitoring vulnerabilities on your device and providing MITRE mapping, GravityZone Security for Mobile also gives visibility into understanding when it might be a good time to renew some of those non-updateable devices.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mobileSecurity/endToEnd.png',
          },
        },
        {
          title: translator.translateFn('Integrated with GravityZone'),
          description: getParagraph(
            translator.translateFn(
              'Security for Mobile is a cloud-only add-on with mobile agents for iOS and Android (Android agents also support Chromebooks) and a chrome extension to secure chrome extensions on Chromebooks.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mobileSecurity/integrated.png',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.IntegrityMonitor]: {
      productItem: {
        type: InProductTrialTypeEnum.IntegrityMonitor,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.IntegrityMonitor),
        description: descriptions[InProductTrialTypeEnum.IntegrityMonitor],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/integrityMonitoring/integrityMonitoringHeader.jpg',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            "Create a new policy that applies the newly available capabilities. Choose the endpoints you'll be testing the new protection features on, deploy the Integrity Monitoring module using a reconfigure agent task, and apply the policy."
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Manage change control'),
          description: getParagraph(
            translator.translateFn(
              'Changes of critical systems or file modifications are often an early indicator that an attacker has gained access to the environment. GravityZone Integrity Monitoring assesses changes in real-time, allowing security teams to stay on top of incident management.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/integrityMonitoring/scanning.jpg',
          },
        },
        {
          title: translator.translateFn('Mitigate risk due to unauthorized changes'),
          description: getParagraph(
            translator.translateFn(
              'View integrity events in real-time as they occur. Identify meaningful configuration changes as they happen and distinguish between approved vs. unapproved changes.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/integrityMonitoring/ruleSets.jpeg',
          },
        },
        {
          title: translator.translateFn('Prioritize alert severity'),
          description: getParagraph(
            translator.translateFn(
              'Alerts can be very noisy and overwhelming, but GravityZone Integrity Monitoring provides teams with automatic actionable recommendations tied to rules allowing teams to act on events. Security teams can filter by categorized events to quickly drill down to the most critical events.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/integrityMonitoring/correctiveActions.jpeg',
          },
        },
        {
          title: translator.translateFn('Straightforward Configuration'),
          description: getParagraph(
            translator.translateFn(
              'Reduce time and effort in configuring integrity monitoring. Eliminate assigning rules or policies manually to endpoints every time. Limiting CPU utilization to scan files in a sequence, pause between scans or simply with lowest priority for a longer interval.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/integrityMonitoring/performanceOptimization.jpeg',
          },
        },
        {
          title: translator.translateFn('Unify and streamline security'),
          description: getParagraph(
            translator.translateFn(
              'Unify and streamline security solutions under one platform including FIM, server workload security, container security, endpoint protection and responses and XDR to bring together device intelligence across the enterprise network.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/integrityMonitoring/security.jpeg',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.ContainerProtection]: {
      productItem: {
        type: InProductTrialTypeEnum.ContainerProtection,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.ContainerProtection),
        description: descriptions[InProductTrialTypeEnum.ContainerProtection],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/securityForContainers/securityForContainersHeader.jpg',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy and flexible deployment'),
          text: translator.translateFn(
            'Deployed on a server or as a standalone container, Container Security will provide Antimalware, EDR Sensor and Advanced Anti-Exploit protection to both the host and all managed containers.'
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Comprehensive security stack built for containers and Linux'),
          description: getParagraph(
            translator.translateFn(
              'While other products are limited to scanning for known vulnerabilities or threats and concentrate on Windows and the end-user, Bitdefender uses a security stack built to protect containers and Linux hosts at runtime. It identifies 0-days exploits, anomalies and Linux TTPs, as well targeted containers, and enables quick investigation and response.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForContainers/protection.jpeg',
          },
        },
        {
          title: translator.translateFn('High security efficacy'),
          description: getParagraph(
            translator.translateFn(
              'Bitdefender demonstrated top efficacy in the 2022 MITRE ATT&CK evaluations, detecting 100% of the attack techniques for Linux.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForContainers/completeInformation.jpeg',
          },
        },
        {
          title: translator.translateFn('Consolidated visibility and protection across infrastructures'),
          description: getParagraph(
            translator.translateFn(
              'Avoid adding new point solutions and consolidate security with the GravityZone cloud workload security platform. It ensures broad threat visibility and protection, covering containers in IaaS and PaaS infrastructures, VMs, cloud workloads, end-user and server, Linux and Windows, private and public clouds.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForContainers/gravityZone.jpeg',
          },
        },
        {
          title: translator.translateFn('Extensive security automation and compatibility'),
          description: getParagraph(
            translator.translateFn(
              'Preserve DevOps agility and operational efficiency with a highly-performant agent, automated security deployment and scaling, and kernel-independent Linux security that enables upgrades to new distributions without sacrificing security or creating problems.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForContainers/performing.jpeg',
          },
        },
        {
          title: translator.translateFn('Prevention, protection, detection and response'),
          description: getParagraph(
            translator.translateFn(
              'Depending on the other active product licenses, you will be able to secure containers and Linux servers using technologies such as Endpoint Risk Analytics to identify and remediate misconfigurations, Patch Management for Linux to keep container hosts up-to-date, Tunable Machine Learning protecting against advanced attacks, advanced Linux anti-exploit and Endpoint Detection and Response.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForContainers/efficiency.jpg',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.StorageProtection]: {
      productItem: {
        type: InProductTrialTypeEnum.StorageProtection,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.StorageProtection),
        description: descriptions[InProductTrialTypeEnum.StorageProtection],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/securityForStorage/securityForStorageHeader.jpg',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            "Create a new policy that applies the newly available capabilities. Choose the endpoints you'll be testing the new protection features on, deploy the Security for Storage module using a reconfigure agent task, and apply the policy."
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Broad compatibility'),
          description: getParagraph(
            translator.translateFn(
              'Security for Storage supports Nutanix Files (AFS), Citrix ShareFile, and ICAP-compliant NAS solutions by Dell{reg}, EMC{reg}, IBM{reg}, Hitachi{reg}, HPE{reg}, Oracle{reg}, and others. If your organization has a variety of ICAP-compatible devices, GravityZone can protect your multi-vendor NAS estate from the same deployment, reducing the cost of storage-security infrastructure.',
              {
                reg: '&reg',
              }
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForStorage/unmatchedProtection.jpeg',
          },
        },
        {
          title: translator.translateFn('Award-winning security, high availability'),
          description: getParagraph(
            translator.translateFn(
              "Leverages Bitdefender's award-winning security technology to provide multiple layers of defense (including machine learning, heuristics, signatures, and cloud lookup) against known and unknown file-based infections that may be part of zero-day or advanced attacks. Policy-based load balancing across the ICAP servers eliminates single points of failure at the system level, ensuring uninterrupted security and high availability, as configured and controlled by the ICAP client."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForStorage/management.jpeg',
          },
        },
        {
          title: translator.translateFn('Unlimited scalability'),
          description: getParagraph(
            translator.translateFn(
              "GravityZone's web-scale, modular architecture lets you add a virtually unlimited number of ICAP servers on demand to support the increasing number of scan requests as your organization's workforce and data."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForStorage/scalability.jpeg',
          },
        },
        {
          title: translator.translateFn('Simple, streamlined administration'),
          description: getParagraph(
            translator.translateFn(
              "Administer Security for Storage from the same central console as other GravityZone solutions; it's fast to deploy and easy to manage. Security for Storage is also highly configurable, so you can quickly define policies and exclusions, set up default actions, create custom reports, and trigger real-time notifications to match your organization's requirements."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/securityForStorage/availability.jpeg',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.MdrEnterprise]: {
      productItem: {
        type: InProductTrialTypeEnum.MdrEnterprise,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.MdrEnterprise),
        description: descriptions[InProductTrialTypeEnum.MdrEnterprise],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        button5: buttonsSettings.button5,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/mdr/mdrHeader.jpg',
      },
      cardItems: [],
      accordionItems: [
        {
          title: translator.translateFn('24x7 Monitoring & Response'),
          description: getParagraph(
            translator.translateFn(
              "Whether it's monitoring the dark web, analyzing your environment, identifying threat hunting trends or responding to and mitigating attacks, our team of experts is on the frontline protecting your business every hour of every day."
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mdr/monitoring.jpg',
          },
        },
        {
          title: translator.translateFn('Threat Hunting'),
          description: getParagraph(
            translator.translateFn(
              'Threat Hunting forms the backbone of our managed detection and response service, and we proactively and continuously source threat intel and conduct threat hunts in your environment to keep your organization safe.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mdr/threat.jpg',
          },
        },
        {
          title: translator.translateFn('Dedicated Security Account Manager'),
          description: getParagraph(
            translator.translateFn(
              'We provide you with a designated member of the Bitdefender MDR team to help you onboard and address any questions or concerns you might have with our MDR service. Your Security Account Manager will be your partner for whatever you need, whenever you need it.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mdr/security.jpg',
          },
        },
        {
          title: translator.translateFn('GravityZone Platform'),
          description: getParagraph(
            translator.translateFn(
              "With Bitdefender MDR, you get all the benefits of our award-winning GravityZone platform, including endpoint detection and response, automated remediation, and host-based firewall and web control. Plus, you'll get a team of security analysts and threat intel researchers to help protect your organization."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mdr/gravityZonePlatform.jpg',
          },
        },
        {
          title: translator.translateFn('GravityZone XDR for MDR'),
          description: getParagraph(
            translator.translateFn(
              'GravityZone XDR for MDR combines advanced threat protection with out-of-the-box analytics across endpoint, cloud, identity, and network, as well as a rich security context for correlation of disparate alerts, quick triage of incidents, investigation across complex environments, and attack containment through automatic and human-led guided response.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mdr/gravityZoneXdr.jpg',
          },
        },
        {
          title: translator.translateFn('Brand and IP Protection'),
          description: getParagraph(
            translator.translateFn(
              "Bitdefender MDR's threat intelligence experts perform specific activities to monitor for risks and breaches of your company data in known repositories used by bad actors. You're alerted in the case of any exposure, and these discoveries trigger threat hunts in your environments."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/mdr/protection.jpg',
          },
        },
      ],
      accordionItemsFaq: [
        {
          title: translator.translateFn('How are alerts generated?'),
          description: getParagraph(
            translator.translateFn(
              "Data from our GravityZone platform is paired with threat intelligence in Bitdefender's MDR security analytics platform to give analysts contextualized information. This is used to generate alerts that we investigate in the SOC."
            )
          ),
        },
        {
          title: translator.translateFn('What reporting tools can I expect from Bitdefender MDR?'),
          description: getParagraph(
            translator.translateFn(
              'Real-time information on the state of your security operations, summary reports with historical trend data and after-action reports with detailed information on incidents and actions taken provide full transparency of your environment.'
            )
          ),
        },
        {
          title: translator.translateFn('How does Bitdefender MDR help businesses save resources?'),
          description: getParagraph(
            translator.translateFn(
              "Bitdefender's on-demand, fully staffed security operations center (SOC) can efficiently scale with your business to help reduce operational burden and save costs by eliminating the resources needed to build and maintain an in-house SOC."
            )
          ),
        },
        {
          title: translator.translateFn('Why should I consider Bitdefender MDR for my business?'),
          description: getParagraph(
            translator.translateFn(
              "Bitdefender MDR is a modern security operation, fully staffed by third-party certified security experts who manage your security technologies from detection to response. We're focused on protecting your business, so you can focus on whatever you want."
            )
          ),
        },
      ],
    },
    [InProductTrialTypeEnum.Cws]: {
      productItem: {
        type: InProductTrialTypeEnum.Cws,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.Cws),
        description: descriptions[InProductTrialTypeEnum.Cws],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        button5: buttonsSettings.button5,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/cloudSecurity/cspmHeader.jpg',
      },
      cardItems: [],
      accordionItems: [
        {
          title: translator.translateFn('Risk Mitigation'),
          description: getParagraph(
            translator.translateFn(
              'By offering comprehensive security coverage, the potential attack surface can be reduced, significantly limiting the chances of successful breaches or data breaches.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/cloudSecurity/risk.jpg',
          },
        },
        {
          title: translator.translateFn('Assured Regulatory Compliance'),
          description: getParagraph(
            translator.translateFn(
              'Automated compliance features guide consistent adherence toward industry standards, enhancing organizational reputation and mitigating potential risk of penalties.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/cloudSecurity/compliance.jpg',
          },
        },
        {
          title: translator.translateFn('Effortless Administration'),
          description: getParagraph(
            translator.translateFn(
              'A consolidated dashboard enhances operational efficiency by presenting a unified overview of security protocols, streamlining both management and incident response procedures.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/cloudSecurity/administration.jpg',
          },
        },
        {
          title: translator.translateFn('Continuous Business Operations'),
          description: getParagraph(
            translator.translateFn(
              'Robust protection of cloud-native applications ensures seamless service delivery, preserving business continuity even amidst emerging threats.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/cloudSecurity/operations.jpg',
          },
        },
      ],
    },
    [InProductTrialTypeEnum.ThreatIntel]: {
      productItem: {
        type: InProductTrialTypeEnum.ThreatIntel,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
        description: descriptions[InProductTrialTypeEnum.ThreatIntel],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        button5: buttonsSettings.button6,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/ti/tiHeader.jpg',
      },
      cardItems: [],
      accordionItems: [
        {
          title: translator.translateFn('Unique insights into the latest threats'),
          description:
            getParagraph(
              translator.translateFn(
                "Fend off attacks before they're even launched with unique insights into elusive APTs (Advanced Persistent Threats), C2 (Command and Control) servers, actively exploited vulnerabilities, and more. "
              )
            ) +
            getParagraph(
              translator.translateFn(
                "Bitdefender's TI feeds and APIs deliver data with actionable threat context like actors, malware families, Mitre TTPs, associated vulnerabilities, confidence, and severity and popularity scores."
              )
            ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/ti/insights.jpg',
          },
        },
        {
          title: translator.translateFn('Quality sources for accurate intelligence'),
          description:
            getParagraph(
              translator.translateFn(
                "Bitdefender's threat intelligence is built with artefacts extracted from a global network of millions of endpoints, gateways, and cloud workloads, spread throughout any industry or company size."
              )
            ) +
            getParagraph(
              translator.translateFn(
                'On top, Bitdefender collects TI from web crawling systems, email traps, honeypots, monitored botnets, data shared with industry partners and law enforcement agencies, and a virtual machine farm that executes over 200,000 malware samples per day.'
              )
            ),

          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/ti/sources.jpg',
          },
        },
        {
          title: translator.translateFn('Cyber threat intelligence expertise'),
          description: getParagraph(
            translator.translateFn(
              'Augment your security capabilities with the expertise of Bitdefender Cyber-Threat Intelligence Labs, a global R&D leader in tests and investigation, with over 100 patents issued for core technologies in the past three years alone. Our 800+ researchers and developers take a layered approach to cybersecurity, using artificial intelligence, deep learning and anomaly-based detection to ensure the accuracy of our threat intelligence feeds.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/ti/expertise.jpg',
          },
        },
        {
          title: translator.translateFn('One-stop shop for the Security Operations Analyst: IntelliZone'),
          description:
            getParagraph(
              translator.translateFn(
                "Bitdefender's IntelliZone is an all-in-one tool for analysts and researchers. This threat intelligence portal consolidates already-vetted data, and the associated threat actors into a single pane of glass for security analysts."
              )
            ) +
            getParagraph(
              translator.translateFn(
                "IntelliZone also includes access to Bitdefender's Sandbox Analyzer for next-gen dynamic malware analysis, and visualization tools for researchers that need to delve deep into a threat, attacker, vulnerable industry, or more."
              )
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/ti/actionableTi.jpg',
          },
        },
        {
          title: translator.translateFn('Robust integration ecosystem'),
          description:
            getParagraph(
              translator.translateFn(
                'Bitdefender {productName} seamlessly integrates with top threat intelligence platforms (TIPs), SIEMs and SOAR applications, including Anomali, MISP servers, or ThreatQuotient. The integrations are implemented to take advantage of platform-specific features, freeing the user from configuring or managing any API changes.',
                {
                  productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
                }
              )
            ) +
            getParagraph(
              translator.translateFn(
                'Bitdefender TI is available in a proprietary JSON-based format called "Bitdefender Threats" but also as STIX 2.0 and MISP formats for partners already using any of these formats.'
              )
            ),

          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/ti/integration.jpg',
          },
        },
      ],
      accordionItemsFaq: [
        {
          title: translator.translateFn('What are the sources of Bitdefender Threat Intelligence data?', {
            productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
          }),
          description: getParagraph(
            translator.translateFn(
              "Bitdefender Threat Intelligence data is collected from a large variety of sources such as Bitdefender's global install base of hundreds of million endpoints in consumer, business and technology licensing, dark web monitoring services, web crawling systems, email traps, honeypots and data from monitored botnets, advanced heuristics techniques and content analysis.",
              {
                productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
              }
            )
          ),
        },
        {
          title: translator.translateFn('What does the Bitdefender {productName} solution include?', {
            productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
          }),
          description:
            getParagraph(
              translator.translateFn(
                "The Bitdefender {productName} solution includes TI feeds and APIs offering enriched intelligence on ransomware, APTs, C2 servers, phishing-and-fraud operations, or mobile threats. Additionally, partners can use IntelliZone, Bitdefender's one-stop-shop for SOC analysts, to access Operational TI, Bitdefender's Sandbox, and more.",
                {
                  productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
                }
              )
            ) +
            getParagraph(
              translator.translateFn(
                'Partners can also ingest Reputation TI from Bitdefender, which is raw data about Indicators recent status, delivered with minimal enrichment, in real-time and in large quantities to equip automated filtering solutions.'
              )
            ),
        },
        {
          title: translator.translateFn('Is the Bitdefender {productName} solution integrated with TIPs/SIEMs/SOARs?', {
            productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
          }),
          description: getParagraph(
            translator.translateFn(
              "Yes, Bitdefender's {productName} is integrated with the main TIPs and SIEMs, like Anomali or MISP.",
              {
                productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ThreatIntel),
              }
            )
          ),
        },
      ],
    },
    [InProductTrialTypeEnum.NadAndEraAddon]: {
      productItem: {
        type: InProductTrialTypeEnum.NadAndEraAddon,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.NadAndEraAddon),
        description: descriptions[InProductTrialTypeEnum.NadAndEraAddon],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/nadAndEra/nadAndEraHeader.png',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            "Create a new policy that applies the newly available capabilities. Choose the endpoints you'll be testing the new protection features on, deploy the Network Attack Defense module using a Reconfigure agent task and apply the policy."
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      hasOutlineAroundAccordionItems: false,
      accordionItems: [
        {
          title: translator.translateFn('Comprehensive Threat Prevention'),
          isOpen: true,
          description: getParagraph(
            translator.translateFn(
              'By inspecting incoming, outgoing, and lateral network traffic, it safeguards against attacks such as brute force attempts, port scans, password theft, and unauthorized lateral movements within the network.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/nadAndEra/comprehensiveThreatPrevention.png',
          },
        },
        {
          title: translator.translateFn('Comprehensive Risk Visibility'),
          description: getParagraph(
            translator.translateFn(
              "Gain a complete view of your organization's attack surface, encompassing endpoint misconfigurations, application vulnerabilities, and user-related risks, all consolidated into a unified security posture overview."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/nadAndEra/comprehensiveRiskVisibility.png',
          },
        },
        {
          title: translator.translateFn('Centralized Management'),
          description: getParagraph(
            translator.translateFn(
              'Designed for ease of use, the add-on integrates seamlessly with your existing Bitdefender GravityZone platform for centralized management. It works automatically in the background, giving you peace of mind without the need for specialized cybersecurity knowledge.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/nadAndEra/centralizedManagement.png',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.WebAccessAndDeviceControlAddon]: {
      productItem: {
        type: InProductTrialTypeEnum.WebAccessAndDeviceControlAddon,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.WebAccessAndDeviceControlAddon),
        description: descriptions[InProductTrialTypeEnum.WebAccessAndDeviceControlAddon],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        button1: buttonsSettings.button1,
        button2: buttonsSettings.button2,
        button3: buttonsSettings.button3,
        button4: buttonsSettings.button4,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/webAccessAndDeviceControl/webAccessAndDeviceControlHeader.png',
      },
      cardItems: [
        commonCardItems.instantRegistrationAddon,
        {
          cardName: translator.translateFn('Easy setup'),
          text: translator.translateFn(
            "Create a new policy that applies the newly available capabilities. Choose the endpoints you'll be testing the new protection features on, deploy the Content Control and Device Control modules using a Reconfigure agent task and apply the policy."
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Enhanced Security'),
          isOpen: true,
          description:
            getParagraph(
              translator.translateFn(
                'Blocks access to malicious and inappropriate websites, reducing the risk of malware infections.'
              )
            ) +
            getParagraph(
              translator.translateFn(
                'Prevents malware introduction by restricting the use of unauthorized external devices.'
              )
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/webAccessAndDeviceControl/enhancedSecurity.png',
          },
        },
        {
          title: translator.translateFn('Data Protection'),
          description:
            getParagraph(
              translator.translateFn('Scans web traffic to prevent unauthorized disclosure of sensitive information.')
            ) +
            getParagraph(
              translator.translateFn(
                'Restricts the transfer of sensitive data through external devices, minimizing data leakage risks.'
              )
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/webAccessAndDeviceControl/dataProtection.png',
          },
        },
        {
          title: translator.translateFn('Improved Productivity'),
          description:
            getParagraph(
              translator.translateFn('Limits access to non-work-related websites, helping employees remain focused.')
            ) +
            getParagraph(
              translator.translateFn('Ensures only work-appropriate devices are used, avoiding distractions.')
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/webAccessAndDeviceControl/improvedProductivity.png',
          },
        },
        {
          title: translator.translateFn('Regulatory Compliance'),
          description: getParagraph(
            translator.translateFn(
              'The Web Access and Device Control add-on supports compliance with data protection laws by restricting access to risky websites and controlling device usage. This helps ensure your business meets regulatory standards and avoids penalties.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/webAccessAndDeviceControl/regulatoryCompliance.png',
          },
        },
        {
          title: translator.translateFn('Centralized Management'),
          description: getParagraph(
            translator.translateFn(
              'The Web Access and Device Control add-on easily  integrates into the GravityZone Control Center, allowing centralized configuration and monitoring, which simplifies policy enforcement across endpoints.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/webAccessAndDeviceControl/centralizedManagement.png',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.licensingStatus,
        commonAccordionItemsFaq.termsOfUse,
        commonAccordionItemsFaq.reactivation,
        commonAccordionItemsFaq.extraInformation,
      ],
    },
    [InProductTrialTypeEnum.ComplianceManager]: {
      productItem: {
        type: InProductTrialTypeEnum.ComplianceManager,
        title: getTranslatedInProductTrialType(InProductTrialTypeEnum.ComplianceManager),
        description: descriptions[InProductTrialTypeEnum.ComplianceManager],
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        button5: buttonsSettings.button5,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/complianceManager/complianceManagerHeader.jpg',
      },
      cardItems: [],
      accordionItems: [
        {
          title: translator.translateFn('Supports major compliance standards'),
          isOpen: true,
          description:
            getParagraph(
              translator.translateFn(
                'Out-of-the-box support for compliance frameworks, including GDPR, PCI DSS, NIS 2 Directive, CISv8, SOC 2, CMMC 2.0, HIPAA, ISO 27001 and more*.'
              )
            ) +
            translator.translateFn(
              '{HTML_TAG_START} *The standards, guidelines and baselines that GravityZone Compliance Manager takes into consideration are listed in a limited extent in the Compliance Manager section of your GravityZone console under the Risk Management menu. {HTML_TAG_END}',
              {
                HTML_TAG_START: '<p class="trial-disclaimer">',
                HTML_TAG_END: '</p>',
              }
            ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/complianceManager/supportComplianceStandard.jpg',
          },
        },
        {
          title: translator.translateFn('Detailed risk and asset insights'),
          description: getParagraph(
            translator.translateFn('View detailed information about risks and affected assets.')
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/complianceManager/detailedRisk.jpg',
          },
        },
        {
          title: translator.translateFn('Enhanced asset visibility'),
          description: getParagraph(
            translator.translateFn('Drill down into affected assets through the dedicated Risk Management section. ')
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/complianceManager/enhancedAsset.jpg',
          },
        },
        {
          title: translator.translateFn('Close identified compliance gaps'),
          description: getParagraph(
            translator.translateFn('Quickly fix detected compliance gaps with the click of a button.')
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/complianceManager/closedIdentified.jpg',
          },
        },
        {
          title: translator.translateFn('Comprehensive report generation'),
          description: getParagraph(
            translator.translateFn('Easily generate detailed PDF or XLSX reports from the Compliance page.')
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/complianceManager/comprehensiveReport.jpg',
          },
        },
      ],
      accordionItemsFaq: [
        complianceAccordionItemsFaq.requirements,
        complianceAccordionItemsFaq.licensingOptions,
        complianceAccordionItemsFaq.attackSurface,
      ],
    },
  };
}

function getMspProductTrialFlagToLandingPageConfigMapping(translator: BaseWebComponentTranslator) {
  const buttonsSettings = getButtonSettingsForMsp(translator);

  // common items
  const commonAccordionItemsFaq = getCommonFaqAccordionItemsForProductItemForMsp(translator);
  const commonCardItems = getCommonCardItemsForProductItem(translator);

  return {
    [MspInProductTrialId.EdrTrial]: {
      productItem: {
        type: MspInProductTrialId.EdrTrial,
        title: translator.translateFn('Endpoint Detection and Response'),
        description:
          translator.translateFn(
            'Make use of extended threat detection, conduct focused investigations, and execute effective response strategies to effectively defend against potential cyber-attacks.'
          ) +
          '\n' +
          translator.translateFn(
            'EDR performs event correlation and is designed to identify advanced threats or attacks that are currently in progress, and assist incident response teams in their efforts to investigate and respond to advanced threads'
          ),
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        isDemoAvailable: true,
        button1: buttonsSettings.startButton,
        button2: buttonsSettings.demoButton,
        button3: buttonsSettings.howToButton,
        button4: buttonsSettings.stopButton,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/msp/edr/header.png',
      },
      cardItems: [
        commonCardItems.easyToStart,
        {
          cardName: translator.translateFn('Quick configuration'),
          text: translator.translateFn(
            'Add the EDR module and apply a new policy to the endpoints of your clients. Enjoy the benefits which come with the additional protection.'
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('Advanced Risk Analytics'),
          description: getParagraph(
            translator.translateFn(
              'Leverage the power of our cutting-edge risk analytics technology to thoroughly assess both endpoints and human behavior within your organization. Benefit from continuous analysis of hundreds of factors to effectively identify, prioritize, and receive actionable guidance on mitigating user, network, and endpoint risks.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/edr/advanced-risk-analytics.jpeg',
          },
        },
        {
          title: translator.translateFn('Industry-leading Threat Detection'),
          description: getParagraph(
            translator.translateFn(
              'Effectively identify and respond to advanced threats such as fileless attacks, ransomware, and zero-day threats in real time. Take advantage of our threat analytics and cloud-based event collector to ensure continuous monitoring of your endpoints.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/edr/threat-detection.jpeg',
          },
        },
        {
          title: translator.translateFn('Cross-endpoint Detection and Response'),
          description: getParagraph(
            translator.translateFn(
              'Employ the power of cross-endpoint correlation technology to enhance threat detection and visibility in our endpoint detection and response (EDR) security solution. Detect and mitigate advanced attacks across multiple endpoints in hybrid infrastructures, including workstations, servers, and containers, running different operating systems.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/edr/cross-endpoint-response.jpg',
          },
        },
        {
          title: translator.translateFn('Visualization at the Organization Level'),
          description: getParagraph(
            translator.translateFn(
              'Utilize comprehensive visuals of adversary actions, enhanced with context and threat intelligence, to effectively identify critical breach paths and alleviate the workload on IT staff. Identify and address gaps in protection measures and assess the impact of incidents on compliance.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/edr/visualization.jpg',
          },
        },
        {
          title: translator.translateFn('Streamlined Investigation and Response'),
          description: getParagraph(
            translator.translateFn(
              "Enhance your IT staff's ability to comprehend breach paths and detect vulnerabilities, by harnessing the innovative visualizations, rich context, and threat intelligence offered by Bitdefender EDR. Empower your team with the tools they need to stay one step ahead of cyber threats."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/edr/streamlined-investigation.jpg',
          },
        },
        {
          title: translator.translateFn('Time-Saving Reporting and Alerting'),
          description: getParagraph(
            translator.translateFn(
              "Employ our configurable dashboards, email notifications, and comprehensive reporting capabilities to streamline your IT team's workflow. Manage everything from a centralized console, saving valuable time and effort. Don't miss out on this opportunity to optimize your team's efficiency."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/edr/reporting.jpg',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.existingMonthlySubscription,
        commonAccordionItemsFaq.eligibleCompanies,
        commonAccordionItemsFaq.policyChanges,
        commonAccordionItemsFaq.mspIptInfo,
        {
          title: translator.translateFn('What features does EDR require to effectively protect my network?'),
          description: getParagraph(
            translator.translateFn(
              'To ensure you receive optimal protection from the EDR feature, it is necessary to utilize GravityZone Cloud MSP Security, which includes complementary access to Advanced Threat Security (HyperDetect, and Sandbox Analyzer features).'
            )
          ),
        },
      ],
    },
    [MspInProductTrialId.MdrTrial]: {
      productItem: {
        type: MspInProductTrialId.MdrTrial,
        title: translator.translateFn('Managed Detection and Response'),
        description:
          translator.translateFn(
            "Empower a skilled team of experts to monitor security for your customers' business 24/7, identify threats, prevent attacks, and provide customized response actions and recommendations."
          ) +
          '\n' +
          translator.translateFn(
            'Enhance overall security outcomes by getting access to advanced tools and efficient triage processes, and assist your customers in effectively addressing their security vulnerabilities.'
          ),
        tooltipTextMapping: buttonsSettings.tooltipTextMapping,
        isHowToAvailable: true,
        isDemoAvailable: true,
        button1: buttonsSettings.startButton,
        button2: buttonsSettings.demoButton,
        button3: buttonsSettings.howToButton,
        button4: buttonsSettings.stopButton,
        imageAlignment: ImgPosition.RIGHT,
        imageSrc: 'app/webcomponents/assets/images/trial/msp/mdr/header.jpg',
      },
      cardItems: [
        commonCardItems.easyToStart,
        {
          cardName: translator.translateFn('Quick configuration'),
          text: translator.translateFn(
            'Enhance your GravityZone Business Security with MDR services and start enjoying the benefits which come with the additional protection.'
          ),
          icon: 'icon-trial_card_2',
        },
        commonCardItems.forHelp,
      ],
      accordionItems: [
        {
          title: translator.translateFn('24x7 analyst-led monitoring and response'),
          description: getParagraph(
            translator.translateFn(
              'We eliminate the operational overhead of managing security events by providing context-based response and tactical recommendations to mitigate threats.'
            )
          ),
          isOpen: true,
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/mdr/01_featuresbenefits.jpg',
          },
        },
        {
          title: translator.translateFn('Threat intel-based hunting'),
          description: getParagraph(
            translator.translateFn(
              "We research cyber threats, geopolitical activity, and vertical-specific data trends and apply this knowledge to proactively hunt in your customers' environments."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/mdr/02_featuresbenefits.jpg',
          },
        },
        {
          title: translator.translateFn('Streamlined onboarding'),
          description: getParagraph(
            translator.translateFn(
              'We allow for the sequential onboarding of multiple companies, providing you with a straightforward process once the first company is enrolled.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/mdr/03_featuresbenefits.jpg',
          },
        },
        {
          title: translator.translateFn('Customized communication'),
          description: getParagraph(
            translator.translateFn(
              'We offer constant communication via email notifications, as well as digests of customer events, available in the MDR Portal.'
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/mdr/04_featuresbenefits.jpg',
          },
        },
        {
          title: translator.translateFn('GravityZone Platform'),
          description: getParagraph(
            translator.translateFn(
              "With Bitdefender MDR, you get all the benefits of our award-winning GravityZone platform, including endpoint detection and response, automated remediation, and host-based firewall and web control. In addition, you'll get a team of security analysts and threat intel researchers to help protect your organization."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/mdr/05_featuresbenefits.jpg',
          },
        },
        {
          title: translator.translateFn('Brand and IP Protection'),
          description: getParagraph(
            translator.translateFn(
              "Bitdefender MDR's threat intelligence experts perform specific activities to monitor for risks and breaches of your company data in known repositories used by bad actors. You're alerted in the case of any exposure, and these discoveries trigger threat hunts in your environments."
            )
          ),
          image: {
            alt: 'Placeholder',
            src: 'app/webcomponents/assets/images/trial/msp/mdr/06_featuresbenefits.jpg',
          },
        },
      ],
      accordionItemsFaq: [
        commonAccordionItemsFaq.existingMonthlySubscription,
        commonAccordionItemsFaq.eligibleCompanies,
        commonAccordionItemsFaq.policyChanges,
        commonAccordionItemsFaq.mspIptInfo,
        {
          title: translator.translateFn('What specific cybersecurity threats does MDR Foundations for MSPs address?'),
          description: getParagraph(
            translator.translateFn(
              'Our MDR Foundations service is tailored to protect against a range of cybersecurity threats that commonly target small and medium size businesses, including malware, ransomware, and phishing attacks. We focus on the specific challenges faced by you and your clients.'
            )
          ),
        },
        {
          title: translator.translateFn(
            'What support is available for MSPs in case of complex cybersecurity incidents?'
          ),
          description: getParagraph(
            translator.translateFn(
              'In the event of complex cybersecurity incidents, our expert team is ready to provide strategic advice and support, helping you manage and resolve these incidents effectively. MDR Foundations provides incident and breach response that supports you and your customers in all scenarios. Once our SOC team performs an investigation and confirms malicious activity, we will automatically take action to stop the intrusion.'
            )
          ),
        },
        {
          title: translator.translateFn('What features does the service require to effectively protect my network?'),
          description: getParagraph(
            translator.translateFn(
              "To ensure optimal protection for your network while utilizing our MDR Foundations service, you need to use GravityZone Cloud MSP Security with Advanced Threat Security and EDR add-ons. These components are provided free of charge for the duration of the trial, and form the critical base for sophisticated threat detection and response, allowing our MDR service to maximize its effectiveness in safeguarding both your and your clients' environments."
            )
          ),
        },
      ],
    },
  };
}

function getLandingPageTranslationsForCountdownToReasonMapping(): {
  [reason in InProductTrialCannotBeStartedReason]: CountdownTranslations | undefined;
} {
  const translator = BaseWebComponentTranslator.getInstance();

  return {
    [InProductTrialCannotBeStartedReason.AlreadyStartedForCurrentProduct]: {
      titleMore: translator.translateFn('Time left:'),
      titleLess: translator.translateFn('Time left, less than:'),
      day: translator.translateFn('Day'),
      days: translator.translateFn('Days'),
    },
    [InProductTrialCannotBeStartedReason.AlreadyStartedForAnotherProduct]: undefined,
    [InProductTrialCannotBeStartedReason.CoolDownNotPassedForStart]: {
      titleMore: translator.translateFn('You can start a new trial in:'),
      titleLess: translator.translateFn('You can start a new trial in less than:'),
      day: translator.translateFn('Day'),
      days: translator.translateFn('Days'),
    },
    [InProductTrialCannotBeStartedReason.NotEnoughDaysForCurrentProduct]: undefined,
    [InProductTrialCannotBeStartedReason.CoolDownNotPassedForRequest]: undefined,
  };
}

function getMspLandingPageTranslationsForCountdownToReasonMapping(): {
  [reason in MspInProductTrialCannotBeStartedReason]: CountdownTranslations | undefined;
} {
  const translator = BaseWebComponentTranslator.getInstance();
  return {
    [MspInProductTrialCannotBeStartedReason.TrialValid]: undefined,
    [MspInProductTrialCannotBeStartedReason.ParentDoesNotResellProduct]: undefined,
    [MspInProductTrialCannotBeStartedReason.CompanyDoesNotHaveEndpointSecurityAsProductType]: undefined,
    [MspInProductTrialCannotBeStartedReason.StatusIsNotAvailable]: undefined,
    [MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct]: {
      titleMore: translator.translateFn('Time left:'),
      titleLess: translator.translateFn('Time left, less than:'),
      day: translator.translateFn('Day'),
      days: translator.translateFn('Days'),
    },
    [MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForAnotherProduct]: undefined,
    [MspInProductTrialCannotBeStartedReason.InCooldown]: {
      titleMore: translator.translateFn('You can start a new trial in:'),
      titleLess: translator.translateFn('You can start a new trial in less than:'),
      day: translator.translateFn('Day'),
      days: translator.translateFn('Days'),
    },
    [MspInProductTrialCannotBeStartedReason.CompanyIsSuspended]: undefined,
    [MspInProductTrialCannotBeStartedReason.ParentCompanyDoesNotDelegateTrialFeature]: undefined,
    [MspInProductTrialCannotBeStartedReason.CompanyDoesNotBelongInAnEligibleTreeHierarchy]: undefined,
    [MspInProductTrialCannotBeStartedReason.UserHasNoManageInventoryRight]: undefined,
    [MspInProductTrialCannotBeStartedReason.ParentCompanyHasNoContact]: undefined,
  };
}

// get landing page configuration for trial type
export function getLandingPageConfigForTrialType(
  type: AvailableProductTrialType,
  replacedKey?: string
): GzLandingPageConfig {
  const translator = BaseWebComponentTranslator.getInstance();
  const productTrialTypeToLandingPageConfig = getProductTrialTypeToLandingPageConfigMapping(translator);
  const landingPageConfigForTrialType = productTrialTypeToLandingPageConfig[type];

  // replace first card with another one containing old license key
  if (replacedKey) {
    landingPageConfigForTrialType.cardItems[0] = {
      cardName: translator.translateFn('A temporary new license'),
      text: translator.translateFn(
        'Your {bold}{replacedKey}{/bold} license key is replaced with a new one that has the same number of seats. Your original license key will be restored automatically when the trial period ends or if you decide to manually cancel the trial.',
        {
          replacedKey: replacedKey,
          bold: '<strong>',
          '/bold': '</strong>',
        }
      ),
      icon: 'icon-trial_card_1',
    };
  }

  return landingPageConfigForTrialType;
}

// get MSP landing page configuration for addon flag

export function getMspLandingPageConfigForAddonFlag(addonFlag) {
  const translator = BaseWebComponentTranslator.getInstance();
  const mspProductTrialFlagToLandingPageConfig = getMspProductTrialFlagToLandingPageConfigMapping(translator);
  const landingPageConfigForAddonFlag = mspProductTrialFlagToLandingPageConfig[addonFlag];
  return landingPageConfigForAddonFlag;
}

export function getTranslationsForCountdownByReason(
  cannotBeStartedReason: InProductTrialCannotBeStartedReason
): CountdownTranslations | undefined {
  const translationForCountdown = getLandingPageTranslationsForCountdownToReasonMapping();
  return translationForCountdown[cannotBeStartedReason];
}

export function getMspTranslationsForCountdownByReason(
  cannotBeStartedReason: MspInProductTrialCannotBeStartedReason
): CountdownTranslations | undefined {
  const translationFoeCountdown = getMspLandingPageTranslationsForCountdownToReasonMapping();
  return translationFoeCountdown[cannotBeStartedReason];
}

export function getMdrOnboardingTranslations() {
  const translator = BaseWebComponentTranslator.getInstance();
  return {
    mdrOnboardingMessage: translator.translateFn('MDR onboarding is in progress.'),
    mdrOnboardingTooltip: translator.translateFn('The Product Trial will start once the service is operational.'),
  };
}
